import { useMemo } from "react";
import { useUsersByUserIds } from "./useUsersByUserIds";
import { getUserDisplayName } from "@/utilities/user/getUserDisplayName";
import { isRealDriverId } from "@/utilities/user/isRealDriverId";

export interface DriverNamesByIds {
  names: Map<string, string>;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

/**
 * Display names for the drivers a set of rows references, looked up by id in one batched
 * `Get User` per 5000 ids ({@link useUsersByUserIds}). Not group-filtered, so a driver assigned
 * outside the selected groups is still named. Placeholder ids are skipped; `undefined` (rows not
 * known yet) reads nothing. A user without any display name is named by id, never blank.
 *
 * `isLoading` stays true while a changed id list loads behind the previous names.
 */
export function useDriverNamesByIds(
  driverIds: readonly (string | null | undefined)[] | undefined,
): DriverNamesByIds {
  const ids = driverIds?.filter(isRealDriverId);
  const { data, isLoading, isPlaceholderData, isError, error } = useUsersByUserIds(ids);

  const names = useMemo(() => {
    const map = new Map<string, string>();
    for (const user of data ?? []) {
      map.set(user.id, getUserDisplayName(user) || user.id);
    }
    return map;
  }, [data]);

  return { names, isLoading: isLoading || isPlaceholderData, isError, error: error ?? null };
}
